import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SHARED_ENVIRONMENT } from '@k12/environment';
import { map, Observable } from 'rxjs';
import {
  SchoolContact,
  SchoolResponse,
  SchoolRosterFilter,
  AttendanceTypesResponse,
  SchoolTermGradesResponse,
  SchoolGradeYearDto,
  StudentRosterResponse,
  GetStudentRostersResponse,
  EnrollmentResponse,
  SchoolTypeResponse,
  SchoolStatusResponse,
  SchoolContactsResponse,
  SchoolTermsResponse,
  UpdateSchoolRosterStudentRequest,
  UpdateSchoolRosterStudentResponse,
  NewEditSchoolContactResponse,
  ApiResponse,
  WithdrawalReasonsResponse,
  CoEnrollmentTypesResponse,
  StartSchoolStudentWithdrawalRequest,
  GetStudentRosterResponse,
  UpdateSchoolModel,
  School,
  RosterImportResult,
  SchoolChangeRequest,
  StartCostCorrectionRequest,
  InstitutionFilter,
  InstitutionDto,
  PagedResultDto,
  CreateInstitutionRequest,
  CreateInstitutionResponse,
  IssueHistory,
  IssueRecordFilter,
  IssueRestrictedFields
} from '@k12/models';

@Injectable({
  providedIn: 'root'
})
export class SchoolsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;

  getGradeLevelsForDropdown(): Observable<SchoolTypeResponse> {
    return this.http.get<SchoolTypeResponse>(`${this.apiUrl}/schools/GetGradeLevelsForDropDown`);
  }

  getSchoolTypesForDropdown(search: string, activeOnly: boolean = false): Observable<SchoolTypeResponse> {
    const params = {
      search: search,
      activeOnly: activeOnly
    };
    return this.http.get<SchoolTypeResponse>(`${this.apiUrl}/schools/GetSchoolTypesForDropDown`, {
      params
    });
  }

  getSchoolStatusesForDropdown(activeOnly: boolean = true): Observable<SchoolStatusResponse> {
    return this.http.get<SchoolStatusResponse>(`${this.apiUrl}/schools/GetSchoolStatusesForDropDown`, {
      params: { activeOnly }
    });
  }

  getSchoolsForDropdown(search: string, activeOnly: boolean = false): Observable<SchoolResponse> {
    const params = {
      search: search,
      activeOnly: activeOnly
    };
    return this.http.get<SchoolResponse>(`${this.apiUrl}/schools/GetSchoolsForDropDown`, {
      params
    });
  }

  getSchoolTermGrades(programPartnerId: string, termDate: Date = new Date()): Observable<SchoolTermGradesResponse> {
    const iso = termDate.toISOString();
    const url = `${this.apiUrl}/schools/${programPartnerId}/grades/${encodeURIComponent(iso)}`;
    return this.http.get<SchoolTermGradesResponse>(url);
  }

  getSchoolTerms(programPartnerId: string): Observable<SchoolTermsResponse> {
    const url = `${this.apiUrl}/schools/${programPartnerId}/terms`;
    return this.http.get<SchoolTermsResponse>(url);
  }

  getSchoolGradeYears(
    programPartnerId: string,
    academicYearId: number
  ): Observable<{ success: boolean; entity: SchoolGradeYearDto[]; message?: string }> {
    // CFIK12.Portal returns the bare array; the Enrollment Functions app wraps the same route in a
    // GenericResponse. Normalise so this holds up whichever one the gateway routes /schools to.
    const url = `${this.apiUrl}/schools/${programPartnerId}/grade-years/${academicYearId}`;
    return this.http
      .get<SchoolGradeYearDto[] | { success: boolean; entity: SchoolGradeYearDto[]; message?: string }>(url)
      .pipe(map((response) => (Array.isArray(response) ? { success: true, entity: response } : response)));
  }

  getAttendanceTypes(): Observable<AttendanceTypesResponse> {
    const url = `${this.apiUrl}/schools/attendance-types`;
    return this.http.get<AttendanceTypesResponse>(url);
  }

  getCoEnrollmentTypes(): Observable<CoEnrollmentTypesResponse> {
    const url = `${this.apiUrl}/schools/co-enrollment-types`;
    return this.http.get<CoEnrollmentTypesResponse>(url);
  }

  getIssueRecords(filter: IssueRecordFilter): Observable<IssueHistory[]> {
    return this.http.post<IssueHistory[]>(`${this.apiUrl}/schools/issueRecords`, filter);
  }

  /**
   * Returns just the actions an active issue currently blocks for a school. Preferred over
   * getIssueRecords for gating UI, which returns the full issue threads including SEAA-internal
   * bodies and history.
   */
  getSchoolIssueRestrictions(programPartnerId: string): Observable<IssueRestrictedFields> {
    return this.http.get<IssueRestrictedFields>(
      `${this.apiUrl}/schools/${programPartnerId}/issue-restrictions`
    );
  }

  getWithdrawalReasonsForSchool(): Observable<WithdrawalReasonsResponse> {
    const url = `${this.apiUrl}/schools/withdrawal-reasons/1`;
    return this.http.get<WithdrawalReasonsResponse>(url);
  }

  getWithdrawalReasonsForHousehold(): Observable<WithdrawalReasonsResponse> {
    const url = `${this.apiUrl}/schools/withdrawal-reasons/3`;
    return this.http.get<WithdrawalReasonsResponse>(url);
  }

  getStudentRoster(studentEnrollmentId: string, academicYearId?: number | null): Observable<GetStudentRosterResponse> {
    const url = `${this.apiUrl}/student-rosters/${studentEnrollmentId}`;

    if (academicYearId === null || academicYearId === undefined) {
      return this.http.get<GetStudentRosterResponse>(url);
    }

    const params = new HttpParams().set('academicYearId', academicYearId.toString());

    return this.http.get<GetStudentRosterResponse>(url, { params });
  }

  getStudentRosters(filters: SchoolRosterFilter): Observable<GetStudentRostersResponse> {
    return this.http.get<GetStudentRostersResponse>(`${this.apiUrl}/student-rosters`, {
      params: this.buildRosterParams(filters)
    });
  }

  getNotYetCertifiedRosters(filters: SchoolRosterFilter): Observable<StudentRosterResponse> {
    return this.http.get<StudentRosterResponse>(`${this.apiUrl}/student-rosters/not-certified`, {
      params: this.buildRosterParams(filters)
    });
  }

  getNotYetEndorsedRosters(filters: SchoolRosterFilter): Observable<StudentRosterResponse> {
    return this.http.get<StudentRosterResponse>(`${this.apiUrl}/student-rosters/not-endorsed`, {
      params: this.buildRosterParams(filters)
    });
  }

  getEndorsedRosters(filters: SchoolRosterFilter): Observable<StudentRosterResponse> {
    return this.http.get<StudentRosterResponse>(`${this.apiUrl}/student-rosters/endorsed`, {
      params: this.buildRosterParams(filters)
    });
  }

  getGraduationRosters(filters: SchoolRosterFilter): Observable<StudentRosterResponse> {
    return this.http.get<StudentRosterResponse>(`${this.apiUrl}/student-rosters/graduation`, {
      params: this.buildRosterParams(filters)
    });
  }

  updateSchoolRosterStudent(request: UpdateSchoolRosterStudentRequest): Observable<UpdateSchoolRosterStudentResponse> {
    return this.http.put<UpdateSchoolRosterStudentResponse>(`${this.apiUrl}/student-rosters/update`, request);
  }

  getTransferWithdrawalRosters(filters: SchoolRosterFilter): Observable<StudentRosterResponse> {
    return this.http.get<StudentRosterResponse>(`${this.apiUrl}/student-rosters/transfers-withdrawals`, {
      params: this.buildRosterParams(filters)
    });
  }

  cancelTransferWithdrawal(studentEnrollmentId: string): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/student-rosters/cancel-transfer/${studentEnrollmentId}`, {});
  }

  private buildRosterParams(filters: SchoolRosterFilter): HttpParams {
    let params = new HttpParams().set('schoolId', filters.schoolId);

    if (filters.enrollmentStatusId !== undefined && filters.enrollmentStatusId !== null) {
      params = params.set('enrollmentStatusId', filters.enrollmentStatusId.toString());
    }
    if (filters.schoolGradeId) {
      params = params.set('schoolGradeId', filters.schoolGradeId);
    }
    if (filters.schoolTermId) {
      params = params.set('schoolTermId', filters.schoolTermId);
    }
    if (filters.studentId) {
      params = params.set('studentId', filters.studentId);
    }
    if (filters.pageNumber !== undefined && filters.pageNumber !== null) {
      params = params.set('pageNumber', filters.pageNumber.toString());
    }
    if (filters.pageSize !== undefined && filters.pageSize !== null) {
      params = params.set('pageSize', filters.pageSize.toString());
    }
    if (filters.orderBy) {
      params = params.set('orderBy', filters.orderBy);
    }
    if (filters.orderDirection) {
      params = params.set('orderDirection', filters.orderDirection);
    }
    if (filters.searchTerm) {
      params = params.set('searchTerm', filters.searchTerm);
    }

    if (filters.academicYearId) {
      params = params.set('academicYearId', filters.academicYearId.toString());
    }

    return params;
  }

  // Get all contacts for a school
  getSchoolContacts(id: string, includeArchived: boolean = false, isInstitutionScope: boolean = false): Observable<SchoolContactsResponse> {
    return this.http.get<SchoolContactsResponse>(`${this.apiUrl}/schools/${id}/contacts`, {
      params: new HttpParams()
        .set('includeArchived', includeArchived.toString())
        .set('isInstitutionScope', isInstitutionScope.toString())
    });
  }

  // Create a new school contact
  createSchoolContact(contact: SchoolContact): Observable<NewEditSchoolContactResponse> {
    return this.http.post<NewEditSchoolContactResponse>(`${this.apiUrl}/schools/contacts`, contact);
  }

  // Delete a school contact
  deleteSchoolContact(parentId: string, personId: string, isInstitutionScope: boolean = false): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this.apiUrl}/schools/${parentId}/contacts/${personId}`, {
      params: new HttpParams().set('isInstitutionScope', isInstitutionScope.toString())
    });
  }

  // Update an existing school contact
  updateSchoolContact(contact: SchoolContact): Observable<NewEditSchoolContactResponse> {
    return this.http.put<NewEditSchoolContactResponse>(`${this.apiUrl}/schools/contacts`, contact);
  }

  promoteContactToHDMA(programPartnerId: string, personId: string, isInstitutionScope: boolean = false): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(
      `${this.apiUrl}/schools/${programPartnerId}/contacts/${personId}/promote-hdma`,
      {},
      {
        params: new HttpParams().set('isInstitutionScope', isInstitutionScope.toString())
      }
    );
  }

  startStudentWithdrawal(request: StartSchoolStudentWithdrawalRequest): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/student-rosters/withdrawal`, request);
  }

  startCostCorrection(request: StartCostCorrectionRequest): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/student-rosters/cost-correction`, request);
  }

  updateSchool(programPartnerId: string, updateSchoolModel: UpdateSchoolModel): Observable<{ success: boolean; entity?: School; message?: string }> {
    const url = `${this.apiUrl}/schools/${programPartnerId}`;
    return this.http.put<{ success: boolean; entity?: School; message?: string }>(
      url,
      updateSchoolModel
    );
  }

  exportNotYetCertifiedRoster(
    schoolId: string,
    mini: boolean,
    schoolTermId?: string | null,
    academicYearId?: number | null
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/student-rosters/not-certified/export`, {
      params: this.buildExportParams(schoolId, mini, schoolTermId, academicYearId),
      responseType: 'blob',
      observe: 'response'
    });
  }

  exportNotYetEndorsedRoster(
    schoolId: string,
    mini: boolean,
    schoolTermId?: string | null,
    academicYearId?: number | null
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/student-rosters/not-endorsed/export`, {
      params: this.buildExportParams(schoolId, mini, schoolTermId, academicYearId),
      responseType: 'blob',
      observe: 'response'
    });
  }

  exportEndorsedRoster(
    schoolId: string, 
    mini: boolean, 
    schoolTermId?: string | null,
    academicYearId?: number | null
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/student-rosters/endorsed/export`, {
      params: this.buildExportParams(schoolId, mini, schoolTermId, academicYearId),
      responseType: 'blob',
      observe: 'response'
    });
  }

  exportGraduationRoster(
    schoolId: string,
    mini: boolean,
    schoolTermId?: string | null,
    academicYearId?: number | null
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/student-rosters/graduation/export`, {
      params: this.buildExportParams(schoolId, mini, schoolTermId, academicYearId),
      responseType: 'blob',
      observe: 'response'
    });
  }

  exportTransferWithdrawalRoster(
    schoolId: string,
    mini: boolean,
    schoolTermId?: string | null,
    academicYearId?: number | null
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.apiUrl}/student-rosters/transfers-withdrawals/export`, {
      params: this.buildExportParams(schoolId, mini, schoolTermId, academicYearId),
      responseType: 'blob',
      observe: 'response'
    });
  }

  importRoster(schoolId: string, file: File, schoolTermId?: string | null): Observable<RosterImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, string> = { schoolId };
    if (schoolTermId) params['schoolTermId'] = schoolTermId;
    return this.http.post<RosterImportResult>(`${this.apiUrl}/student-rosters/import`, formData, { params });
  }

  private buildExportParams(
    schoolId: string,
    mini: boolean,
    schoolTermId?: string | null,
    academicYearId?: number | null
  ): Record<string, string | boolean | number> {
    const params: Record<string, string | boolean | number> = { schoolId, mini };
    if (schoolTermId) params['schoolTermId'] = schoolTermId;
    if (academicYearId) params['academicYearId'] = academicYearId;
    return params;
  }

  changeSchoolForStudent(request: SchoolChangeRequest): Observable<EnrollmentResponse> {
    return this.http.post<EnrollmentResponse>(`${this.apiUrl}/schools/change`, request);
  }

  getFilteredInstitutions(filter: InstitutionFilter): Observable<PagedResultDto<InstitutionDto>> {
    let params = new HttpParams();

    // Pagination
    const pageNumber = filter.pageNumber ?? 1;
    const pageSize = filter.pageSize ?? 25;
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    // Sorting
    if (filter.orderBy) {
      params = params.set('orderBy', filter.orderBy);
    }
    if (filter.orderDirection) {
      params = params.set('orderDirection', filter.orderDirection);
    }

    // Search
    if (filter.searchTerm) {
      params = params.set('searchTerm', filter.searchTerm);
    }

    if(filter.multiSchoolOnly !== undefined) {
      params = params.set('multiSchoolOnly', filter.multiSchoolOnly.toString());
    }

    return this.http.get<PagedResultDto<InstitutionDto>>(`${this.apiUrl}/schools/GetInstitutions/page`, {
      params
    });
  }

  searchSchoolsByFein(fein: string): Observable<{ success: boolean; entity: School[]; message?: string }> {
    return this.http.get<{ success: boolean; entity: School[]; message?: string }>(
      `${this.apiUrl}/schools/searchbyfein/${encodeURIComponent(fein)}`
    );
  }

  createInstitution(request: CreateInstitutionRequest): Observable<CreateInstitutionResponse> {
    return this.http.post<CreateInstitutionResponse>(`${this.apiUrl}/schools/institutions`, request);
  }

  getInstitution(institutionId: string): Observable<{ success: boolean; entity?: InstitutionDto; message?: string }> {
    return this.http.get<{ success: boolean; entity?: InstitutionDto; message?: string }>(
      `${this.apiUrl}/schools/GetInstitution/${encodeURIComponent(institutionId)}`
    );
  }

  addSchoolsToInstitution(institutionId: string, schoolIds: string[]): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/institution/schools`, {
      institutionId,
      schoolIds: schoolIds.filter((schoolId) => !!schoolId)
    });
  }
}
